/**
 * Interview scheduling wizard shell.
 * Composes Header, Content (tabs/stepper), and Footer.
 *
 * Bulk-self-schedule path: `mode='bulk-self-schedule'` mounts the
 * prerequisite overlay first (interview-plan picker + candidate
 * categorisation). Once resolved, the same wizard renders with the
 * first eligible application's IDs as the "anchor" — drives plan
 * fetch, calendar busy events, slot-email defaults — while the save
 * path swaps in `ats_application_ids[]` via
 * `bulkSelfScheduleContext`.
 *
 * The overlay vs. wizard branch lives at the outer `ScheduleInterview`
 * component; the wizard body is in `ScheduleInterviewWizard` below.
 * Keeping them as distinct components is what lets us early-return
 * the overlay without violating hook order across the overlay→wizard
 * transition (the wizard's hooks all mount fresh once it appears).
 */
import { useCallback, useMemo, useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Button, getCountriesByDialCode } from '@kula-ai/components';
import { CenterSpinner } from '@/components/ats';
import type { ScheduleInterviewProps } from './config/types';
import { SCHEDULE_INTERVIEW_COPY } from './config/copy';
import {
  isBulkSelfScheduleMode,
  isSelfScheduleMode,
} from './config/modeClassifiers';
import { useScheduleInterview } from './useScheduleInterview';
import { ScheduleHeader } from './shell/ScheduleHeader';
import { ScheduleContent } from './shell/ScheduleContent';
import { ScheduleFooter } from './shell/ScheduleFooter';
import { SaveConfirmationDialog } from './editMode';
import { BulkSelfSchedulePrerequisiteOverlay } from './bulkSelfSchedulePrerequisite';
import type { BulkSelfSchedulePrerequisiteResolution } from './bulkSelfSchedulePrerequisite';
// SCH-52: copy + images not finalised — re-enable when design lands.
// import { SchedulingRefreshedAnnouncement } from '@/pages/InterviewManager/components/SchedulingRefreshedAnnouncement';

export const ScheduleInterview = (props: ScheduleInterviewProps) => {
  const isBulk = isBulkSelfScheduleMode(props.mode);
  const [bulkSelfScheduleResolution, setBulkSelfScheduleResolution] =
    useState<BulkSelfSchedulePrerequisiteResolution | null>(null);
  const handleBulkSelfScheduleResolve = useCallback(
    (resolution: BulkSelfSchedulePrerequisiteResolution) =>
      setBulkSelfScheduleResolution(resolution),
    []
  );

  // Single content branch so the one-time announcement at the bottom of
  // this return mounts once per wizard open across every mode — manual,
  // adhoc, self-schedule, edit, send-additional-slots, and bulk
  // (including its prerequisite overlay step). Hook order is preserved
  // because the existing useState/useCallback above run before any
  // branching.
  let content: React.ReactNode;
  if (isBulk && !bulkSelfScheduleResolution) {
    if (!props.bulkSelfScheduleProps) {
      // Defensive — every bulk-self-schedule call site must wire
      // `bulkSelfScheduleProps` (see `JobCandidates.tsx`). Fail loud
      // rather than silently mounting a wizard we can't categorise
      // candidates for.
      content = (
        <Flex
          flex="1"
          minH={0}
          h="100%"
          w="100%"
          align="center"
          justify="center"
          flexDirection="column"
          gap="4"
        >
          <Text color="secondary.700">
            Bulk self-schedule launched without a candidate selection.
          </Text>
          <Button variant="secondary" onClick={() => props.onClose?.()}>
            Close
          </Button>
        </Flex>
      );
    } else {
      content = (
        <BulkSelfSchedulePrerequisiteOverlay
          bulkSelfScheduleProps={props.bulkSelfScheduleProps}
          onResolve={handleBulkSelfScheduleResolve}
          onCancel={() => props.onClose?.()}
        />
      );
    }
  } else {
    content = (
      <ScheduleInterviewWizard
        {...props}
        bulkSelfScheduleResolution={bulkSelfScheduleResolution}
      />
    );
  }

  return (
    <>
      {content}
      {/* SCH-52: copy + images not finalised — re-enable when design lands. */}
      {/* Fires once per browser across every scheduling mode (manual, */}
      {/* adhoc, self-schedule, bulk, send-additional-slots) thanks to */}
      {/* the localStorage gate inside SchedulingRefreshedAnnouncement. */}
      {/* <SchedulingRefreshedAnnouncement /> */}
    </>
  );
};

interface ScheduleInterviewWizardProps extends ScheduleInterviewProps {
  /**
   * Resolved bulk-self-schedule prerequisite (anchor candidate +
   * eligible IDs). Always `null` for non-bulk modes; always non-null
   * for bulk by the time the wizard mounts (the outer shell
   * early-returns the overlay otherwise).
   */
  bulkSelfScheduleResolution: BulkSelfSchedulePrerequisiteResolution | null;
}

const ScheduleInterviewWizard = ({
  mode,
  apiReference = 'create',
  applicationId,
  candidateId,
  jobId,
  stageId,
  stageActivityId,
  onClose,
  onSuccess,
  interviewId,
  scheduleId,
  candidateName,
  candidateAvatar,
  candidateEmail,
  jobTitle,
  applicationData,
  additionalSlotsContext,
  interviewPlatform,
  bulkSelfScheduleResolution,
}: ScheduleInterviewWizardProps) => {
  // Bulk swaps the caller-supplied single-candidate IDs for the anchor
  // application's IDs (first eligible candidate). The save path
  // re-expands to `ats_application_ids[]` via the bulk-self-schedule
  // context — see `useScheduleSave` +
  // `payload/mapBulkSelfScheduleContext.ts`.
  //
  // `jobId` is **not** overridden from `bulkSelfScheduleResolution` —
  // the resolution describes things the overlay knows (stage action,
  // eligible applications, anchor contact). The actual `ats_job_id`
  // always flows from the wizard prop (= `JobCandidates.tsx` `jobId`
  // URL param). Substituting the stage id in here previously broke
  // `/calendar_events/trigger_fetch` with `err_ats_job_not_found`.
  const isBulk = !!bulkSelfScheduleResolution;
  const effectiveApplicationId =
    bulkSelfScheduleResolution?.anchorApplicationId ?? applicationId;
  const effectiveCandidateId =
    bulkSelfScheduleResolution?.anchorContactId ?? candidateId;
  const effectiveStageId = bulkSelfScheduleResolution?.stageId ?? stageId;
  const effectiveStageActivityId =
    bulkSelfScheduleResolution?.stageActivityId ?? stageActivityId;
  const effectiveJobId = jobId;
  const effectiveCandidateEmail =
    bulkSelfScheduleResolution?.anchorCandidateEmail ?? candidateEmail;

  const { candidatePhone, candidatePhoneValue } = useMemo(() => {
    // `GET /ats_applications/:id` returns `contact.phone_number` +
    // `contact.phone_code` (not `local_phone_number` — that was a
    // guessed name that silently made the phone look empty). Both are
    // plain strings; `phone_code` may carry a leading `+`.
    const contact = (applicationData?.contact ?? {}) as {
      phone_code?: string;
      phone_number?: string;
    };
    const local = contact.phone_number?.trim() ?? '';
    const code = contact.phone_code?.trim() ?? '';
    if (!local) {
      return { candidatePhone: '', candidatePhoneValue: undefined };
    }
    // PhoneInput keys off `countryCode` (ISO alpha-2) — resolve it from
    // the dial code so the flag + formatting match the stored number.
    const matches = getCountriesByDialCode(code);
    const resolvedCountryCode = matches.length > 0 ? matches[0].code : '';
    return {
      candidatePhone: code ? `${code} ${local}` : local,
      candidatePhoneValue: {
        countryCode: resolvedCountryCode,
        dialCode: code.startsWith('+') ? code : code ? `+${code}` : '',
        number: local,
      },
    };
  }, [applicationData]);

  const bulkSelfScheduleContext = useMemo(
    () =>
      bulkSelfScheduleResolution && typeof jobId === 'number'
        ? {
            applicationIds: bulkSelfScheduleResolution.applicationIds,
            // `source_job_id` on the bulk payload is the **job** the
            // candidates were selected from — same value the rest of
            // the wizard uses for `ats_job_id`. Read it from the prop
            // instead of mirroring it through the resolution.
            sourceJobId: jobId,
          }
        : undefined,
    [bulkSelfScheduleResolution, jobId]
  );

  const {
    config,
    payloadRef,
    getStepRef,
    isSaving,
    scheduleNow,
    prefillVersion,
    interviewPlan,
    currentUserZoomStatus,
    planZoomHostType,
    showEditLoader,
    showEditError,
    showCreateLoader,
    dialogVariant,
    isDialogOpen,
    dialogFlags,
    dialogInterviewers,
    confirmDialog,
    cancelDialog,
    activeStep,
    isBusy,
    onStepChange,
    goNextOrComplete,
    goBack,
    canGoBack,
    primaryButtonLabel,
    backButtonLabel,
    lastStepKey,
  } = useScheduleInterview({
    mode,
    apiReference,
    applicationId: effectiveApplicationId,
    candidateId: effectiveCandidateId,
    jobId: effectiveJobId,
    stageId: effectiveStageId,
    stageActivityId: effectiveStageActivityId,
    interviewId,
    scheduleId,
    initialCandidatePhone: candidatePhoneValue,
    bulkSelfScheduleContext,
    interviewPlatform,
    onClose,
    onSuccess,
  });

  const isEdit = apiReference === 'edit';
  // `additionalSlotsContext` being set means the wizard was opened via
  // "Send additional slots" on a confirmed self-schedule interview.
  // The underlying flow is a regular edit; this flag only drives copy
  // + the booked-slot highlight.
  const isAdditionalSlots = Boolean(additionalSlotsContext);
  const isSelfSchedule = isSelfScheduleMode(mode);
  const headerTitle = isAdditionalSlots
    ? SCHEDULE_INTERVIEW_COPY.header.sendAdditionalSlots
    : isEdit
      ? isSelfSchedule
        ? SCHEDULE_INTERVIEW_COPY.header.editSelfSchedule
        : SCHEDULE_INTERVIEW_COPY.header.edit
      : isSelfSchedule
        ? SCHEDULE_INTERVIEW_COPY.header.createSelfSchedule
        : SCHEDULE_INTERVIEW_COPY.header.create;

  // Prefer the email carried on `applicationData.contact` (authoritative,
  // used by the rest of the ATS). Fallback to the `candidateEmail` prop
  // when applicationData isn't available (some older callers). Bulk
  // overrides this with the anchor candidate's email — but only when a
  // single candidate is being scheduled, otherwise the slot-email step
  // hides the To row entirely.
  const resolvedCandidateEmail = useMemo<string>(() => {
    if (isBulk) return effectiveCandidateEmail || '';
    const contactEmail =
      (applicationData?.contact as { email?: string } | undefined)?.email ?? '';
    return contactEmail || effectiveCandidateEmail || '';
  }, [applicationData, effectiveCandidateEmail, isBulk]);

  const bulkSelfScheduleApplicationCount =
    bulkSelfScheduleResolution?.applicationIds.length;

  // Bulk header avatar stack (legacy `KAvatarList` parity for
  // `SCHEDULE_TYPE_BULK_SELF_SCHEDULE`). Only emit when we're in bulk
  // mode and the prerequisite overlay actually resolved with eligible
  // applications — otherwise the header falls back to the single-
  // candidate name layout.
  const bulkHeaderCandidates = useMemo(() => {
    if (!isBulk) return undefined;
    const eligible = bulkSelfScheduleResolution?.eligibleApplications ?? [];
    if (eligible.length === 0) return undefined;
    return eligible.map((app) => ({
      name:
        app.contact.full_name ||
        [app.contact.first_name, app.contact.last_name]
          .filter(Boolean)
          .join(' ') ||
        app.contact.email ||
        '',
      avatar:
        app.contact.image_url ??
        app.contact.avatar_url ??
        app.contact.avatar_thumb_url,
      description: app.contact.position,
    }));
  }, [isBulk, bulkSelfScheduleResolution]);

  const stepProps = useMemo(
    () => ({
      payloadRef,
      mode,
      apiReference,
      prefillVersion,
      atsJobId: effectiveJobId,
      atsJobStageActionId: effectiveStageActivityId,
      candidatePhone,
      candidatePhoneValue,
      candidateEmail: resolvedCandidateEmail,
      additionalSlotsContext,
      bulkSelfScheduleApplicationCount,
      interviewPlatform,
    }),
    [
      payloadRef,
      mode,
      apiReference,
      prefillVersion,
      effectiveJobId,
      effectiveStageActivityId,
      candidatePhone,
      candidatePhoneValue,
      resolvedCandidateEmail,
      additionalSlotsContext,
      bulkSelfScheduleApplicationCount,
      interviewPlatform,
    ]
  );

  if (showEditError) {
    return (
      <Flex
        flex="1"
        minH={0}
        h="100%"
        w="100%"
        align="center"
        justify="center"
        flexDirection="column"
        gap="4"
        px="6"
      >
        <Text color="secondary.700" textAlign="center">
          This interview could not be loaded.
        </Text>
        <Button variant="secondary" onClick={() => onClose?.()}>
          Close
        </Button>
      </Flex>
    );
  }

  // Every scheduling flow eventually emails the candidate (calendar
  // invite, self-schedule link, or interview reschedule notice). Block
  // up front when the contact has no email on file so the user gets a
  // clear, actionable message instead of a downstream validation
  // error or a backend 4xx after they've filled the form. Bulk skips
  // this — its prereq overlay categorises no-email candidates into
  // the warning bucket and excludes them from the eligible set. Edit
  // mode skips it too: the interview already exists, the user may
  // just want to fix something unrelated, and forcing them to update
  // the contact first would be hostile.
  if (!isBulk && !isEdit && !resolvedCandidateEmail) {
    return (
      <Flex
        flex="1"
        minH={0}
        h="100%"
        w="100%"
        align="center"
        justify="center"
        flexDirection="column"
        gap="4"
        px="6"
      >
        <Text color="secondary.700" textAlign="center" maxW="420px">
          This candidate doesn&apos;t have an email. Add an email to their
          contact before scheduling an interview.
        </Text>
        <Button variant="secondary" onClick={() => onClose?.()}>
          Close
        </Button>
      </Flex>
    );
  }

  const isOnLastStep = activeStep === lastStepKey;
  // On EDIT, collapse the per-step "Next:" stepping into a single direct
  // save (like the email interview activity): every step's primary button
  // is "Save interview" and submits via `scheduleNow` (validates all steps
  // first). Tabs stay clickable for navigation. CREATE keeps the
  // multi-step "Next: <step>" flow with its primary advancing.
  const primaryLabel = isEdit
    ? SCHEDULE_INTERVIEW_COPY.footer.saveInterview
    : primaryButtonLabel;
  const onPrimary = isEdit ? scheduleNow : goNextOrComplete;
  // Secondary CTA (a direct-save shortcut) is only meaningful in CREATE on
  // non-final steps — on edit the primary already does the direct save, so
  // rendering it too would be a visible duplicate.
  const showSecondaryCta = !isEdit && !isOnLastStep && config.showSecondaryCta;
  const secondaryCta = showSecondaryCta
    ? {
        label: SCHEDULE_INTERVIEW_COPY.footer.scheduleNow,
        onClick: scheduleNow,
      }
    : undefined;

  const isHydrating = showEditLoader || showCreateLoader;

  return (
    <Flex
      flex="1"
      flexDirection="column"
      minH={0}
      h="100%"
      w="100%"
      overflow="clip"
      position="relative"
    >
      {isDialogOpen && dialogVariant && (
        <SaveConfirmationDialog
          variant={dialogVariant}
          candidate={{ name: candidateName ?? '', avatar: candidateAvatar }}
          showCandidateInvite={dialogFlags.update_calendar_invite_to_candidate}
          showInterviewerInvite={
            dialogFlags.update_calendar_invite_to_interviewer
          }
          showCandidateEmail={dialogFlags.resend_email_to_candidate}
          showInterviewerEmail={dialogFlags.resend_email_to_interviewer}
          interviewerInviteRecipients={dialogInterviewers}
          interviewerEmailRecipients={dialogInterviewers}
          onConfirm={confirmDialog}
          onCancel={cancelDialog}
          isLoading={isSaving}
        />
      )}
      <ScheduleHeader
        title={headerTitle}
        candidateId={effectiveCandidateId}
        candidateName={candidateName}
        candidateAvatar={candidateAvatar}
        candidates={bulkHeaderCandidates}
        jobTitle={jobTitle}
        applicationData={applicationData}
        stageId={effectiveStageId}
      />

      {isHydrating ? (
        // Keep the wizard chrome (Header + Footer) mounted while edit/plan
        // data lands; only the content area swaps spinner → steps. Mounting
        // steps with a blank payloadRef would fire effects against unset
        // ids — defer the heavy ScheduleContent mount until hydration is
        // done. This avoids the perceived "whole drawer" layout jump on
        // open (spinner-only → full wizard) since header/footer no longer
        // pop into place after the spinner unmounts.
        <Flex flex="1" minH={0} align="center" justify="center">
          <CenterSpinner />
        </Flex>
      ) : (
        <ScheduleContent
          config={config}
          stepProps={stepProps}
          interviewPlan={interviewPlan}
          currentUserZoomStatus={currentUserZoomStatus}
          planZoomHostType={planZoomHostType}
          candidateName={candidateName}
          candidateAvatar={candidateAvatar}
          jobTitle={jobTitle}
          getStepRef={getStepRef}
          activeStep={activeStep}
          onStepChange={onStepChange}
        />
      )}

      <ScheduleFooter
        disabled={isHydrating || isBusy || isSaving}
        canGoBack={canGoBack}
        backButtonLabel={backButtonLabel}
        primaryButtonLabel={primaryLabel}
        loadingText={
          isEdit
            ? SCHEDULE_INTERVIEW_COPY.footer.saving
            : SCHEDULE_INTERVIEW_COPY.footer.scheduling
        }
        onBack={() => void goBack()}
        onCancel={() => onClose?.()}
        // Return the promise so WizardFooter's re-entry guard can
        // await — releasing the lock only once the underlying
        // validate/save chain settles (or `disabled` cycles for the
        // mutation-in-flight path).
        onPrimary={() => onPrimary()}
        secondaryCta={secondaryCta}
      />
    </Flex>
  );
};
